import React, { useCallback } from "react";
import { observer } from "mobx-react-lite";
import { DragDropContext, DraggableLocation, DropResult } from "react-beautiful-dnd";

import "./DragAndDropTable.scss";
import DragAndDropColumn from "./components/DragAndDropColumn";
import {
  Column,
  CustomProperty,
  ProjectPropertyStorage,
} from "../../services/organization/ProjectPropertyStorage";
import { arrayItemMove } from "../../helpers/Collections";
import DataGridNoResults from "../datatable/DataGridNoResults";
import DataGridTotalResults from "../datatable/DataGridTotalResults";
import { translationService } from "../../services/translation/TranslationService";

interface DragAndDropTable {
  itemTypeKey: string;
  storage: ProjectPropertyStorage;
  handlePositionUpdate: () => void;
  rowClickCallback: (item: any) => void;
}

export default observer(function DragAndDropTable(props: DragAndDropTable) {
  const { storage, itemTypeKey, handlePositionUpdate } = props;

  const reorderIndices = useCallback((items: CustomProperty[]) => {
    items.forEach((item, index) => (item.index = index));
  }, []);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      const { destination, source, draggableId } = result;
      const columns = storage.data.columns as Column[];

      const itemIsDroppedInTheSameLocation = (
        source: DraggableLocation,
        destination: DraggableLocation
      ) => {
        return destination.droppableId === source.droppableId && destination.index === source.index;
      };

      if (!destination) {
        return;
      }

      if (itemIsDroppedInTheSameLocation(source, destination)) return;

      const startColumn = columns.find((x) => x.id.toString() === source.droppableId) as Column;
      const destinationColumn = columns.find(
        (x) => x.id.toString() === destination.droppableId
      ) as Column;

      const itemToMove = startColumn.properties.find(
        (x) => x.id.toString() === draggableId
      ) as CustomProperty;

      if (startColumn === destinationColumn) {
        arrayItemMove(
          startColumn.properties,
          startColumn.properties.indexOf(itemToMove),
          destination.index
        );
        reorderIndices(startColumn.properties);
      } else {
        startColumn.properties.splice(source.index, 1);
        reorderIndices(startColumn.properties);
        itemToMove.columnId = destinationColumn.id;
        destinationColumn.properties.splice(destination.index, 0, itemToMove);
        reorderIndices(destinationColumn.properties);
      }

      handlePositionUpdate();
    },
    [handlePositionUpdate, reorderIndices, storage.data.columns]
  );

  if (!storage.itemsCount) {
    return <DataGridNoResults itemTypeKey={itemTypeKey} />;
  }

  return (
    <>
      <DataGridTotalResults
        itemTypeKey={itemTypeKey}
        totalResults={storage.itemsCount}
      />
      <div className="dnd-table-container">
        <div className="dnd-table">
          <div className="dnd-table-header">
            <div className="dnd-table-row">
              <div className="dnd-table-cell align-left">
                {translationService.translate("general.move")}
              </div>
              <div className="row-container">
                <div className="dnd-table-cell">{translationService.translate("general.name")}</div>
                <div className="dnd-table-cell">{translationService.translate("general.type")}</div>
              </div>
            </div>
          </div>
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="dnd-table-body">
              {storage.data.columns.map((column: Column) => {
                return (
                  <DragAndDropColumn
                    key={column.id}
                    column={column}
                    rowClickCallback={props.rowClickCallback}
                  />
                );
              })}
            </div>
          </DragDropContext>
        </div>
      </div>
    </>
  );
});
